import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'

import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'

import constant from 'lodash/fp/constant'
import isEmpty from 'lodash/fp/isEmpty'
import isFunction from 'lodash/fp/isFunction'
import isString from 'lodash/fp/isString'

import type { Progress } from '../../../api/endpoints'
import type { HTTPError, ImageSize, MediaTypes, Upload } from '../../../types'

import { AllowedMediaSources } from '../../../enums'
import { getFullMediaUrl } from '../../../helpers'
import { type HandleFileCallback, handleFiles } from '../../../helpers/files'
import useTranslations from '../../../hooks/useTranslations'
import DisplayError from '../../DisplayError'

const provider = AllowedMediaSources.Paste

type Props = {
  type: MediaTypes
  onPaste: (imageUrl: string, provider: string, size?: ImageSize) => void
  active?: boolean
  enabled?: boolean
  handleFile?: HandleFileCallback
  setIsLoading?: Dispatch<SetStateAction<boolean>>
  setProgress?: Dispatch<SetStateAction<Progress | null>>
}
export default function Paste({
  active = false,
  enabled = true,
  handleFile,
  onPaste,
  setIsLoading,
  setProgress,
  type,
}: Props) {
  const $t = useTranslations()

  const [error, setError] = useState('')
  const displayError = useCallback(
    (message: string, duration = 5000) => {
      setError(message)

      if (duration > 0) {
        setTimeout(() => {
          setError('')
        }, duration)
      }
    },
    [setError],
  )

  const handleFileUpload = useCallback(
    async (files: FileList | null) => {
      if (!files || !enabled || !isFunction(handleFile)) {
        return
      }

      setIsLoading?.(true)

      await handleFiles(
        handleFile,
        [...files].filter(
          ({ type: fileType }) => type === 'file' || fileType.startsWith(type),
        ),
        AllowedMediaSources.Paste,
        {
          onError: (
            reason: string,
            {
              error: fileError,
              file,
              max,
            }: { error: string | Error; file: File; max?: number },
          ) => {
            switch (reason) {
              case 'empty_file': {
                displayError($t(`editor.${type}.loader.paste.error.file_empty`))

                break
              }
              case 'too_big': {
                displayError(
                  $t(`editor.${type}.loader.paste.error.file_too_big`, {
                    maxSize: (max as number) / 1024 / 1024,
                    size: Math.ceil(file.size / 1024 / 1024),
                  }),
                )

                break
              }
              case 'upload_error': {
                displayError($t.http(fileError as HTTPError))

                break
              }
              default: {
                if (!reason.includes('abort')) {
                  displayError(
                    isString(fileError) ? fileError : fileError.toString(),
                  )
                }
              }
            }
          },
          onProcessingSuccess: ({
            image,
            upload,
          }: {
            upload: Upload
            image?: HTMLImageElement
          }) => {
            onPaste(
              getFullMediaUrl(upload?.path),
              provider,
              type === 'image' && image
                ? {
                    height: image.naturalHeight,
                    width: image.naturalWidth,
                  }
                : undefined,
            )
          },
          onProgress: (progress) => setProgress?.(progress),
        },
      )

      setIsLoading?.(constant(false))
      setProgress?.(null)
    },
    [
      $t,
      displayError,
      enabled,
      handleFile,
      onPaste,
      setIsLoading,
      setProgress,
      type,
    ],
  )

  const handlePaste = useCallback(
    (event: ClipboardEvent) => {
      if (!enabled || !active) {
        return
      }

      if (
        event.clipboardData &&
        (event.clipboardData?.files?.length ?? 0) > 0
      ) {
        handleFileUpload(event.clipboardData.files!)
      }
    },
    [handleFileUpload, active, enabled],
  )

  useEffect(() => {
    if (enabled && active) {
      window.addEventListener('paste', handlePaste)
    } else {
      window.removeEventListener('paste', handlePaste)
    }

    return () => {
      window.removeEventListener('paste', handlePaste)
    }
  }, [active, enabled, handlePaste])

  if (!enabled) {
    return false
  }

  return (
    <>
      <Typography variant="caption-median">
        {$t(`editor.${type}.loader.paste_hint`)}
      </Typography>

      {!isEmpty(error) && <DisplayError display message={error} />}
    </>
  )
}
